import { formatInTimeZone } from "date-fns-tz";
import { FC, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppSelector } from "store/hooks";

import PhotoViewer from "./PhotoViewer";

import styles from "./style.module.scss";

const GST_ZONE = "Asia/Dubai";

const formatCreated = (iso: string | null | undefined): string => {
  if (!iso) return "-";
  try {
    return formatInTimeZone(new Date(iso), GST_ZONE, "MMMM d, yyyy");
  } catch {
    return "-";
  }
};

const formatPhone = (phone: string | null | undefined): string =>
  phone ? `+${phone.replace(/\D/g, "")}` : "-";

const AssetInfoTab: FC = () => {
  const { id } = useParams();
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const asset = useAppSelector((state) => state.assets.current);

  if (!asset || asset.id !== Number(id)) return null;

  const fields = [
    { label: "Type", value: asset.type || "-" },
    { label: "Company", value: asset.company_name || "-" },
    { label: "Portfolio", value: asset.portfolio_name || "-" },
    { label: "Created", value: formatCreated(asset.created_at) },
  ];

  const manager = asset.manager;
  const images = asset.images ?? [];

  return (
    <div className={styles.assetInfo}>
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Asset information</h2>

        <div className={styles.fields}>
          {fields.map((field) => (
            <div key={field.label} className={styles.field}>
              <span className={styles.fieldLabel}>{field.label}</span>
              <span className={styles.fieldValue}>{field.value}</span>
            </div>
          ))}
        </div>

        <div className={styles.details}>
          {manager && (
            <div className={styles.box}>
              <span className={styles.boxLabel}>Asset manager</span>

              <div className={styles.manager}>
                <span className={styles.avatar}>{(manager.full_name || "?").charAt(0).toUpperCase()}</span>

                <div className={styles.managerInfo}>
                  <div className={styles.managerName}>
                    <span className={styles.name}>{manager.full_name || "-"}</span>
                    <span className={styles.position}>{manager.position || "-"}</span>
                  </div>

                  <div className={styles.managerContacts}>
                    <span className={styles.contact}>{manager.email || "-"}</span>
                    <span className={styles.contact}>{formatPhone(manager.phone)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {asset.description && (
            <div className={styles.box}>
              <span className={styles.boxLabel}>Description</span>
              <span className={styles.description}>{asset.description}</span>
            </div>
          )}
        </div>
      </section>

      {images.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Photo</h2>

          <div className={styles.photos}>
            {images.map((image, index) => (
              <button
                key={image.id}
                type="button"
                className={styles.photo}
                onClick={() => setViewerIndex(index)}
              >
                <img src={image.url} alt={image.original_name} />
              </button>
            ))}
          </div>
        </section>
      )}

      {viewerIndex !== null && (
        <PhotoViewer images={images} startIndex={viewerIndex} onClose={() => setViewerIndex(null)} />
      )}
    </div>
  );
};

export default AssetInfoTab;
